import type { LocalDecorationId } from '@jira/polaris__domain-field/src/ValueDecoration.types.tsx';
import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';
import { performJiraConfigurationUpdate } from './performJiraConfigurationUpdate.tsx';

type deleteAllValueDecorationsProps = {
	fieldKey: FieldKey;
};

export const deleteValueDecoration =
	(fieldKey: FieldKey, localDecorationId: LocalDecorationId) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, createAnalyticsEvent, onDecorationUpdateFailed }: Props,
	): Promise<void> => {
		const state = getState();
		const { fieldValueDecorations, fields } = state;
		const field = fields[fieldKey];
		const existingValueDecorations = fieldValueDecorations[fieldKey] || [];
		const newValueDecorations = [...existingValueDecorations];

		const decorationIdx = newValueDecorations.findIndex(
			({ localDecorationId: id }) => localDecorationId === id,
		);
		newValueDecorations.splice(decorationIdx, 1);

		setState({
			fieldValueDecorations: {
				...fieldValueDecorations,
				[fieldKey]: newValueDecorations,
			},
		});

		try {
			try {
				await performJiraConfigurationUpdate({
					fieldKey,
					fieldRemote,
					existingDecorations: existingValueDecorations,
					newDecorations: newValueDecorations,
				});

				fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(createAnalyticsEvent({}), {
					issueFieldKey: fieldKey,
					issueFieldType: field.type,
				});
			} catch (error) {
				setState({
					fieldValueDecorations: {
						...getState().fieldValueDecorations,
						[fieldKey]: existingValueDecorations,
					},
				});

				throw error;
			}
		} catch (error) {
			onDecorationUpdateFailed(
				error instanceof Error ? error : new Error('Failed to delete value decoration'),
			);
		}
	};

export const deleteAllValueDecorations =
	({ fieldKey }: deleteAllValueDecorationsProps) =>
	async (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	): Promise<void> => {
		try {
			const state = getState();
			const { fieldValueDecorations, fields } = state;
			const field = fields[fieldKey];
			const existingValueDecorations = fieldValueDecorations[fieldKey] || [];

			setState({
				fieldValueDecorations: {
					...fieldValueDecorations,
					[fieldKey]: [],
				},
			});

			await performJiraConfigurationUpdate({
				fieldKey,
				fieldRemote,
				existingDecorations: existingValueDecorations,
				newDecorations: [],
			});

			fireCompoundAnalyticsEvent.FieldConfig.valueDecorationChanged(createAnalyticsEvent({}), {
				issueFieldKey: fieldKey,
				issueFieldType: field.type,
			});
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			onFieldUpdateFailed(e);
		}
		return undefined;
	};
